import { useEffect, useState, useRef, RefObject } from 'react';
import { Outlet, useMatch, useOutletContext } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import queryString from 'query-string';
import { IconExpand, IconLongArrowLeft } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import MessagesList from '../components/messages-list';
import { useLazyGetMainChannelsByTypeQuery } from './store/services';
import ROUTES from '../routes';
import {
  setCurrentQuery,
  setMainChannelsByTypeIsLoading,
  setMainChannelsLoadingErrorVisible,
  setMainChannels,
} from './store/slices/general';
import { Channel } from './types';

import cn from './messages.module.css';

const MessagesMain = ({
  matchPath,
  path,
}: {
  matchPath: string;
  path: string;
}) => {
  const listScrollRef = useRef<HTMLDivElement | null>(null);

  const {
    general: {
      mainChannels,
      currentQuery,
      mainChannelsPagination,
      mainCnannelsLoadingErrorVisible,
      mainChannelsErrorText,
      employee,
      selectedChannel,
      searchQuery,
    },
  } = useAppSelector((state) => state.messages);
  const [nextChannelsLoading, setNextChannelsLoading] = useState(false);

  const [getMainChannels, { isLoading, isFetching }] =
    useLazyGetMainChannelsByTypeQuery();

  const {
    selectedUserId,
    sendMessage,
    scrollRef,
    search,
    connectionStatus,
    prevConnectionStatus,
  } = useOutletContext<{
    selectedUserId?: string;
    sendMessage: SendMessage;
    scrollRef: RefObject<HTMLDivElement | null>;
    search?: string;
    connectionStatus?: string;
    prevConnectionStatus?: string;
  }>();

  const match = useMatch(matchPath);

  const dispatch = useAppDispatch();

  const getNextChannels = async (skip: string) => {
    setNextChannelsLoading(true);

    const response = await getMainChannels({ skip, query: currentQuery });

    if (response && response.data) {
      setNextChannelsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(
      setMainChannelsByTypeIsLoading(isFetching && !nextChannelsLoading),
    );
  }, [isFetching]);

  useEffect(() => {
    if (
      selectedChannel &&
      mainChannels &&
      !mainChannels?.find((item) => item.id === selectedChannel.id) &&
      (!searchQuery ||
        (searchQuery &&
          (searchQuery === selectedChannel.applicationId ||
            searchQuery === selectedChannel.organizationId)))
    ) {
      const nextMainChannels = ([] as Channel[]).concat(mainChannels);

      nextMainChannels.unshift(selectedChannel);

      dispatch(setMainChannels(nextMainChannels));
    }
  }, [selectedChannel, mainChannels, searchQuery]);

  useEffect(() => {
    if (employee) {
      let parsed;

      if (search) {
        parsed = queryString.parse(search);
      }

      if (
        !search ||
        (search && parsed?.name && currentQuery) ||
        (search && !parsed?.name)
      ) {
        if (path === ROUTES.MESSAGES_MAIN_UNREAD) {
          const nextQueryArray = currentQuery.split('&');

          const filteredQuery = nextQueryArray
            ?.filter(
              (item) =>
                item &&
                !item.includes('unreadMessages') &&
                !item.includes('unreadByUserId') &&
                !item.includes('status') &&
                !item.includes('employeeAssigned'),
            )
            ?.join('&');

          let query = filteredQuery;

          if (!query.includes('unreadMessages')) {
            query = `${
              query ? `${query}&` : ''
            }unreadMessages=true&unreadByUserId=${employee?.id}&status=OPEN`;
          }

          getMainChannels({
            query,
          });

          dispatch(setCurrentQuery(query));
        } else if (path === ROUTES.MESSAGES_MAIN_IN_PROGRESS) {
          const nextQueryArray = currentQuery.split('&');

          const filteredQuery = nextQueryArray
            ?.filter(
              (item) =>
                item &&
                !item.includes('unreadMessages') &&
                !item.includes('unreadByUserId') &&
                !item.includes('status') &&
                !item.includes('employeeAssigned'),
            )
            ?.join('&');

          let query = filteredQuery;

          if (!query.includes('status=OPEN')) {
            query = `${query ? `${query}&` : ''}status=OPEN${
              query.includes('employeeId') ? '' : '&employeeAssigned=true'
            }&unreadMessages=false&unreadByUserId=${employee?.id}`;
          }

          getMainChannels({
            query,
          });

          dispatch(setCurrentQuery(query));
        } else if (path === ROUTES.MESSAGES_MAIN_COMPLETED) {
          const nextQueryArray = currentQuery.split('&');

          const filteredQuery = nextQueryArray
            ?.filter(
              (item) =>
                item &&
                !item.includes('unreadMessages') &&
                !item.includes('unreadByUserId') &&
                !item.includes('status') &&
                !item.includes('employeeAssigned'),
            )
            ?.join('&');

          let query = filteredQuery;

          if (!query.includes('employeeAssigned')) {
            query = `${
              query ? `${query}&` : ''
            }employeeAssigned=false&unreadMessages=false&unreadByUserId=${employee?.id}`;
          }

          getMainChannels({
            query,
          });

          dispatch(setCurrentQuery(query));
        } else if (path === ROUTES.MESSAGES_MAIN_ALL) {
          const nextQueryArray = currentQuery.split('&');

          const filteredQuery = nextQueryArray
            ?.filter(
              (item) =>
                item &&
                !item.includes('unreadMessages') &&
                !item.includes('unreadByUserId') &&
                !item.includes('status') &&
                !item.includes('employeeAssigned'),
            )
            ?.join('&');

          const query = filteredQuery;

          getMainChannels({
            query,
          });

          dispatch(setCurrentQuery(query));
        }
      }
    }
  }, [path, currentQuery, employee, search]);

  useEffect(() => {
    if (
      connectionStatus !== prevConnectionStatus &&
      prevConnectionStatus === 'Closed'
    ) {
      getMainChannels({
        query: currentQuery,
      });
    }
  }, [connectionStatus, prevConnectionStatus, currentQuery]);

  useEffect(() => {
    let title = 'Messages: Main';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  return (
    <>
      <MessagesList
        messages={mainChannels}
        isLoading={
          isLoading || (isFetching && !nextChannelsLoading) || !employee
        }
        matchPath={matchPath}
        path={path}
        userId={employee?.id as string}
        pagination={mainChannelsPagination}
        nextChannelsLoading={nextChannelsLoading}
        scrollRef={listScrollRef}
        getNextChannels={getNextChannels}
        selectedUserId={selectedUserId}
        sendMessage={sendMessage}
      />
      {mainCnannelsLoadingErrorVisible && (
        <div className={cn.messageLoadError}>
          <span>{mainChannelsErrorText}</span>
          <button
            type="button"
            className={cn.buttonClose}
            aria-label="Close button"
            onClick={() => dispatch(setMainChannelsLoadingErrorVisible(false))}
          >
            <IconExpand />
          </button>
        </div>
      )}
      {!match && !isLoading && (
        <div
          className={`${cn.messagesContentRight} ${cn.messagesContentRightEmpty}`}
        >
          <div className={cn.messagesContentRightHint}>
            <IconLongArrowLeft />
            Select a chat from the list
          </div>
        </div>
      )}
      <Outlet
        context={{
          sendMessage,
          scrollRef,
          connectionStatus,
          prevConnectionStatus,
        }}
      />
    </>
  );
};

export default MessagesMain;
